import { useProductContext, ProductId, getProductNamespace } from '@/contexts/ProductContext';

/**
 * Hook to access product context
 *
 * This hook provides easy access to the current product configuration,
 * product switching capabilities, and product comparison utilities.
 *
 * @returns {Object} Product context utilities
 * @property {ProductId} productId - Current product ID (mindcotine or relief)
 * @property {ProductConfig} config - Current product configuration
 * @property {function} isProduct - Check if current product matches given ID
 * @property {function} switchProduct - Switch to a different product
 * @property {function} setCurrentProduct - Set the current product
 *
 * @example
 * const { productId, config, isProduct } = useProduct();
 *
 * if (isProduct(ProductId.MINDCOTINE)) {
 *   // MindCotine-specific logic
 * }
 *
 * // Use product branding
 * <div style={{ color: config.branding.primaryColor }}>
 *   {config.name}
 * </div>
 */
export const useProduct = () => {
  const { currentProduct, productConfig, isProduct, switchProduct, setCurrentProduct } = useProductContext();

  return {
    productId: currentProduct,
    config: productConfig,
    isProduct,
    switchProduct,
    setCurrentProduct,
  };
};

// Re-export ProductId and getProductNamespace for convenience
export { ProductId, getProductNamespace };
